import { Alert, Button, Col, Form, Input, Row, Select, Skeleton, Space } from "antd";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import OrdersApi from "../../../api/OrdersApi";
import { getUSStates, hasPOBox1, hasPOBox2 } from "../../../util/utils";
import "../PrintCheckoutPage";
import { Summary } from "./Summary";

const { Option } = Select;

const ADDRESS_ERROR_ZIP = "Please enter a valid zip code.";
const ADDRESS_ERROR_PO_BOX = "Please provide a physical address, post office boxes not allowed.";

export function Shipping({ order, setOrder, view, setView, isComputingTax, setIsComputingTax, checkoutMessage }: any) {
  const [loading] = useState(false);
  const [hasAddressError, setHasAddressError] = useState(false);
  const [addressError, setAddressError] = useState(ADDRESS_ERROR_ZIP);
  const [isBusinessAddress, setIsBusinessAddress] = useState(order.addressType === "business");

  // const [selectedState, setSelectedState] = useState("");

  const [loadingExpectedDeliveryDate, setLoadingExpectedDeliveryDate] = useState(false);

  const [form] = Form.useForm();

  const checkAddressAndUpdateOrder = useCallback((callback: any) => {
    if (order.state && order.zip && order.zip.length >= 5) {
      if (hasPOBox1(order.addressLine1) || hasPOBox2(order.addressLine2)) {
        setHasAddressError(true);
        setAddressError(ADDRESS_ERROR_PO_BOX);
        return;
      } else {
        setHasAddressError(false);
        setAddressError(ADDRESS_ERROR_ZIP);
      }

      // update order, calculate taxes and edd
      setHasAddressError(false);

      setLoadingExpectedDeliveryDate(true);
      setIsComputingTax(true);


      OrdersApi.updateOrder(order).then((resp) => {
        let orderResp = resp.data;
        // setOrder(resp.data);
        setHasAddressError(resp.data.hasAddressError || false);

        setOrder(orderResp);
        setIsComputingTax(false);

        if (callback) {
          callback();
        }

        if (!resp.data.hasAddressError) {
          OrdersApi.getExpectedDeliveryDate(order.id).then(resp2 => {
            if (resp2.data) {
              if (resp2.data === "conflict") {
                orderResp.edd = "conflict";
              } else {
                // const eddDebug = JSON.parse(resp2.data.debug)
                // console.log("-> EDD Debug:")
                // console.log(eddDebug)

                orderResp.edd = moment(resp2.data.edd, 'YYYYMMDD').format('dddd, MMMM Do');
              }
            }

            setLoadingExpectedDeliveryDate(false);
          });
        } else {
          setIsComputingTax(false);
          setLoadingExpectedDeliveryDate(false);
        }
      })
    } else {
      setIsComputingTax(false);
      setLoadingExpectedDeliveryDate(false);
    }
  }, [order, setIsComputingTax, setOrder]);

  useEffect(() => {
    if (window.location.href.includes('fromTimber=true')) {
      const url = window.location.href.split('?')[0];
      window.history.pushState({ path: url }, "", url);
    }
  }, [order])

  useEffect(() => {
    checkAddressAndUpdateOrder(() => { });
    // eslint-disable-next-line
  }, [])

  function onFinish(values: any) {
    order.addressType = values.addressType;
    order.name = values.name;
    order.businessName = values.businessName;
    order.addressLine1 = values.addressLine1;
    order.addressLine2 = values.addressLine2;
    order.city = values.city;
    order.state = values.state;
    order.zip = values.zip;
    order.phone = values.phone;
    setOrder(order);

    checkAddressAndUpdateOrder(() => {
      setView("payment");
    })
  }

  const onValuesChange = useCallback(() => {
    const formValues = form.getFieldsValue();

    order.addressType = formValues.addressType;
    order.name = formValues.name;
    order.businessName = formValues.businessName;
    order.addressLine1 = formValues.addressLine1;
    order.addressLine2 = formValues.addressLine2;
    order.city = formValues.city;
    order.state = formValues.state;
    order.zip = formValues.zip;
    order.phone = formValues.phone;

    setOrder(order);

    setIsBusinessAddress(formValues.addressType === "business");
    setTimeout(() => {
      form.validateFields().then(values => {
        checkAddressAndUpdateOrder(() => { });
      });
    }, 100);
  }, [form, order, setOrder, checkAddressAndUpdateOrder]);

  return (
    <div className={"print-shipping-page"}>
      {loading && <Skeleton />}
      {!loading && order && (
        <>
          <Row>
            <Col span={12}>
              <div className="shipping">
                <h3>Shipping</h3>
                <h5>Shipping Address</h5>

                <Form
                  form={form}
                  name="product"
                  layout="horizontal"
                  size="large"
                  labelCol={{ span: 4, offset: 0 }}
                  wrapperCol={{ span: 24, offset: 0 }}
                  labelWrap
                  labelAlign="left"
                  requiredMark="optional"
                  initialValues={{ ...order, addressType: order.addressType || null, state: order.state || null }}
                  onFinish={onFinish}
                  onValuesChange={onValuesChange}
                >
                  <Form.Item
                    name="addressType"
                    rules={[
                      {
                        required: true,
                        message: "Please select the address type.",
                      },
                    ]}
                  >
                    <Select placeholder="Address Type">
                      <Option key={"business"}>Business</Option>
                      <Option key={"home"}>Home</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="name"
                    rules={[
                      { required: true, message: "Please input the name." },
                    ]}
                  >
                    <Input placeholder="Name" />
                  </Form.Item>

                  <Form.Item
                    name="businessName"
                    rules={[
                      {
                        required: isBusinessAddress,
                        message: "Please input the business name."
                      }
                    ]}>
                    <Input placeholder="Business Name" />
                  </Form.Item>

                  <Form.Item
                    name="addressLine1"
                    rules={[
                      {
                        required: true,
                        message: "Please input the street address.",
                      },
                    ]}
                  >
                    <Input placeholder="Street Address" />
                  </Form.Item>

                  <Form.Item
                    name="addressLine2"
                  >
                    <Input placeholder="Street Address Line 2" />
                  </Form.Item>

                  <Form.Item
                    name="city"
                    rules={[
                      { required: true, message: "Please input the city." },
                    ]}
                  >
                    <Input placeholder="City" />
                  </Form.Item>

                  <Row>
                    <Col span={13}>
                      <Form.Item
                        name="state"
                        rules={[
                          { required: true, message: "Select a state." },
                        ]}
                        className="state-dropdown"
                      >
                        <select>
                          <option value="" disabled selected>Select a state</option>
                          {getUSStates().map((type) => (
                            <option key={type.key} value={type.key}>{type.value}</option>
                          ))}
                        </select>
                      </Form.Item>
                    </Col>

                    <Col span={10} offset={1}>
                      <Form.Item
                        name="zip"
                        rules={[
                          {
                            required: true,
                            message: "Please input the zip/postal code.",
                          },
                        ]}
                      >
                        <Input placeholder="Zip/Postal Code" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    name="phone"
                    rules={[
                      { required: true, message: "Please input the phone." },
                    ]}
                  >
                    <Input placeholder="Phone" />
                  </Form.Item>

                  {hasAddressError ?
                    <Alert
                      description={addressError}
                      type="error"
                    /> : ''}

                  <Form.Item>
                    <Row justify="space-between">
                      <Col className="footer-btns" style={{ textAlign: 'right' }}>
                        <Space align="baseline">
                          <div className="return-to-previous" onClick={() => setView("cart")}>
                            &lt; Return to Cart
                          </div>
                          <Button type="primary" htmlType="submit" loading={isComputingTax || loadingExpectedDeliveryDate} disabled={loading || isComputingTax || hasAddressError || loadingExpectedDeliveryDate}>
                          {/* <Button type="primary" htmlType="submit"> */}
                            Continue to Payment
                          </Button>
                        </Space>
                      </Col>
                    </Row>

                  </Form.Item>
                </Form>
              </div>
            </Col>

            <Col span={12} className={"desktop-summary"}>
              <Summary order={order} setOrder={setOrder} view={view} isComputingTax={isComputingTax} loadingExpectedDeliveryDate={loadingExpectedDeliveryDate} setLoadingExpectedDeliveryDate={setLoadingExpectedDeliveryDate} checkoutMessage={checkoutMessage} />
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}
